import { FormProvider, useForm } from 'react-hook-form';
import LoginForm, {
  LoginFormData,
} from '../../features/system/login/LoginForm';
import {
  signInWithEmailAndPassword,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { auth, signInWithMicrosoft } from '../../../firebase';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorToast, { showToast } from '../../components/messages/ErrorAlert';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import firebaseErrorMessages from '../../components/errorMessages/firebase';
import LoadingButton from '../../components/inputs/LoadingButton';
import { putUserAuth } from '../../services/admin/UsersService';

const Login = () => {
  const toast = useRef<Toast>(null);
  const formMethods = useForm<LoginFormData>();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSubmit } = formMethods;

  const postLogin = (userUID: string) => {
    const stateUserUID = location.state?.userUID;

    if (stateUserUID === userUID) {
      const { pathname, search } = location.state?.from ?? {};
      const from = pathname ? `${pathname}${search ?? ''}` : '/';
      navigate(from, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: LoginFormData) => {
      return signInWithEmailAndPassword(auth, data.email, data.password);
    },
    onSuccess: (data) => {
      postLogin(data.user.uid);
    },
    onError: (error: { message: string; code: string }) => {
      showToast(
        'error',
        toast,
        'Login Error',
        firebaseErrorMessages[error.code] ||
          'An error occurred, please try again later',
        3000
      );
    },
  });

  const { mutate: userAuth, isLoading: isUserAuthLoading } = useMutation({
    mutationFn: () => {
      return putUserAuth();
    },
    onSuccess: (data) => {
      postLogin(data.authID);
    },
    onError: (error: {
      message: string;
      code: string;
      response: { data: { message: string } };
    }) => {
      showToast(
        'error',
        toast,
        'Microsoft Sign-in Error',
        firebaseErrorMessages[error.code] ||
          error.response.data.message ||
          'An error occurred, please try again later',
        3000
      );
    },
  });

  const { mutate: logInMicrosoft, isLoading: isMicrosoftLoading } = useMutation(
    {
      mutationFn: () => {
        return signInWithMicrosoft();
      },
      onSuccess: (data) => {
        const additionalInfo = getAdditionalUserInfo(data);

        if (additionalInfo?.isNewUser) {
          userAuth();
        } else {
          postLogin(data.user.uid);
        }
      },
      onError: (error) => {
        showToast(
          'error',
          toast,
          'Microsoft Sign-in Error',
          'An error occurred, please try again later',
          3000
        );
      },
    }
  );

  const onSubmit = (data: LoginFormData) => {
    mutate(data);
  };

  return (
    <div
      className="login-page h-screen flex"
      style={{
        backgroundImage: 'url(assets/layout/images/login-background.svg)',
      }}
    >
      <div className="flex align-items-center m-auto justify-content-center">
        <div className="p-4">
          <div className="text-center mb-8">
            <img
              src="./assets/layout/images/bluwai_Logo.png"
              alt="Bluwai"
              className="w-full"
            />
          </div>
          <ErrorToast toastRef={toast} />
          <LoadingButton
            label="Login with Microsoft"
            isLoading={isMicrosoftLoading || isUserAuthLoading}
            onClick={() => logInMicrosoft()}
            className="w-fit bluwaiBlue border-0 flex m-auto py-3 relative"
            fontSize="text-22px"
          />
          <div className="flex align-items-center gap-1 my-4">
            <div className="border-1 w-full dividerBackground dividerShadow h-fit" />
            <div className="text-24px">Or</div>
            <div className="border-1 w-full dividerBackground dividerShadow h-fit" />
          </div>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <LoginForm />
              <Button className="w-15rem buttonSecondary border-0 flex m-auto py-3">
                {isLoading && <ProgressSpinner className="w-3rem h-3rem" />}
                {!isLoading && (
                  <span className="mx-auto text-22px text-white">Log In</span>
                )}
              </Button>
              <Link
                to="/resetPassword"
                className="mt-2 block text-standard totalColor cursor-pointer hover:underline text-center"
              >
                Forgot Password?
              </Link>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default Login;
